<!-- Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance of
this file, via any medium, is strictly prohibited. All information contained
herein is proprietary and confidential and may not be shared with any third
party without the express written consent of Reciprocity, Inc. -->

<template>
  <div class="comments-input">
    <div class="comment-area">
      <editor-content
        ref="editorContentComp"
        class="editor__content"
        :class="{ 'form-control content-editable': isEditable }"
        :editor="editor"
      />
      <div v-if="isEditable" class="pro-tip">
        <span class="pro-tip__title">Pro tip:</span> press <span class="pro-tip__shortcut">Shift+Enter</span> to send
        the comment
      </div>
    </div>

    <div v-show="showSuggestions && isEditable" ref="suggestions" class="suggestion-list">
      <template v-if="hasResults">
        <div
          v-for="(item, index) in filteredItems"
          :key="item.id"
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedItemIndex === index }"
          @click="selectItem(item)"
        >
          <span class="suggestion-list__item__label">
            <profile-icon :model="item" />
            <span class="mention-label-name">{{ item.name }}</span>
          </span>

          <span class="suggestion-list__item__value"><span class="pipe">|</span> {{ item.email }}</span>
        </div>
      </template>
      <div v-else class="suggestion-list__item is-empty">
        {{ emptyResultsText }}
      </div>
    </div>
  </div>
</template>

<script>
import tippy, { sticky } from 'tippy.js';
import { Editor, EditorContent } from 'tiptap';
import { Placeholder, History } from 'tiptap-extensions';
import { ProfileIcon } from 'zenComponents';
import Mention from './mention';
import CustomKeys from './customKeys';

// TODO: allow to customize display and id
export default {
  name: 'CommentsInput',
  components: {
    EditorContent,
    ProfileIcon,
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
    emptyResultsText: {
      type: String,
      default: 'No matching results',
    },
    placeholder: {
      type: String,
      default: 'Write a comment, add @ to mention someone',
    },
    initContent: {
      type: String,
      default: '',
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    customPillClasses: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      editor: new Editor({
        extensions: [
          new Placeholder({
            showOnlyWhenEditable: true,
            showOnlyCurrent: true,
            emptyNodeText: this.placeholder,
          }),
          new CustomKeys({
            onShiftEnter: () => {
              this.onShiftEnterPressed();
            },
          }),
          new History({ newGroupDelay: 100 }),
          new Mention({
            customClasses: this.customPillClasses,
            label: 'name',
            tooltip: 'email',
            items: () => this.items,
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query;
              this.filteredItems = items;
              this.suggestionRange = range;
              this.renderPopup(virtualNode);
              this.insertMention = command;
            },
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query;
              this.filteredItems = items;
              this.suggestionRange = range;
              this.navigatedItemIndex = 0;
              this.renderPopup(virtualNode);
            },
            onExit: () => {
              this.query = null;
              this.filteredItems = [];
              this.suggestionRange = null;
              this.navigatedItemIndex = 0;
              this.destroyPopup();
            },
            onKeyDown: ({ event }) => {
              if (event.key === 'ArrowUp') {
                this.upHandler();
                return true;
              }

              if (event.key === 'ArrowDown') {
                this.downHandler();
                return true;
              }

              if (event.key === 'Enter') {
                this.enterHandler();
                return true;
              }

              return false;
            },
            onFilter: (items, query) => {
              if (!query) {
                return items;
              }

              return items.filter(item => {
                const search = query.toLowerCase();
                return item.name.toLowerCase().includes(search) || item.email.toLowerCase().includes(search);
              });
            },
          }),
        ],
        onUpdate: () => this.onEditorUpdate(),
        autoFocus: 'end',
        content: this.initContent,
        editable: this.isEditable,
        // https://prosemirror.net/docs/ref/#model.ParseOptions.preserveWhitespace
        parseOptions: { preserveWhitespace: 'full' },
      }),
      query: null,
      suggestionRange: null,
      filteredItems: [],
      navigatedItemIndex: 0,
      insertMention: () => {},
    };
  },
  computed: {
    hasResults() {
      return this.filteredItems.length;
    },
    showSuggestions() {
      return this.query || this.hasResults;
    },
  },
  beforeDestroy() {
    this.destroyPopup();
  },
  methods: {
    onShiftEnterPressed() {
      this.$emit('on-shift-enter');
    },
    onEditorUpdate() {
      const element = this.$refs.editorContentComp.$el;
      const html = this.isEditorEmpty() ? '' : this.prepareHtml(this.editor.getHTML());
      this.$emit('on-editor-changed', {
        mentions: this.parseMentions(element),
        html,
      });
    },
    prepareHtml(html) {
      // removes mention name and unnecessary attributes to be stored in db
      // just store the data-mention-id that represents user ID
      const fragment = document.createElement('div');
      fragment.innerHTML = html;
      fragment.querySelectorAll(`span[${Mention.DATA_MENTION_ID}]`).forEach(span => {
        span.removeAttribute('class');
      });

      return fragment.innerHTML;
    },
    parseMentions(element) {
      let mentions = [];
      let childrenObj = element.children;
      if (!childrenObj.length) return [];
      childrenObj = childrenObj[0].querySelectorAll(`[${Mention.DATA_MENTION_ID}]`);
      childrenObj.forEach(elem => {
        const mentionId = elem.getAttribute(Mention.DATA_MENTION_ID);
        const email = this.items.filter(item => item.id.toString() === mentionId.toString());
        if (email.length && !mentions.includes(email[0].email)) {
          mentions.push(email[0].email);
        }
      });

      return mentions;
    },
    isEditorEmpty() {
      // avoid saving empty strings
      return (
        !this.$refs.editorContentComp.$el.textContent ||
        !this.$refs.editorContentComp.$el.textContent.replace(/\s/g, '').length ||
        this.$refs.editorContentComp.$el.innerHTML.includes('is-editor-empty')
      );
    },
    upHandler() {
      this.navigatedItemIndex = (this.navigatedItemIndex + this.filteredItems.length - 1) % this.filteredItems.length;
    },
    downHandler() {
      this.navigatedItemIndex = (this.navigatedItemIndex + 1) % this.filteredItems.length;
    },
    enterHandler() {
      const item = this.filteredItems[this.navigatedItemIndex];
      if (item) {
        this.selectItem(item);
      }
    },
    selectItem(item) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: item.id,
          label: item.name,
        },
      });

      this.editor.focus();
    },
    renderPopup(node) {
      if (this.popup || !this.isEditable) {
        return;
      }

      this.popup = tippy('.content-editable', {
        getReferenceClientRect: node.getBoundingClientRect,
        appendTo: () => document.body,
        interactive: true,
        sticky: true,
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: 'mouseenter',
        showOnCreate: true,
        placement: 'top-start',
        inertia: true,
        maxWidth: 500,
        duration: [400, 200],
        popperOptions: {
          modifiers: {
            preventOverflow: { enabled: false },
            hide: { enabled: false },
          },
        },
      });
    },
    destroyPopup() {
      if (this.popup) {
        if (this.popup[0]) this.popup[0].destroy();
        this.popup = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@/design/variables';

.editor__content {
  overflow: hidden;
}

.comments-input {
  display: flex;
  flex-grow: 1;

  div[contenteditable~='false'] {
    p {
      padding: 0.2rem 0 !important;
    }
  }

  .comment-area {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    margin-right: 5px;

    .pro-tip {
      font-size: 0.8rem;
      align-self: flex-end;
      margin-top: 5px;
      color: $color-regent-gray;
      font-weight: normal;

      &__title {
        font-weight: normal;
      }

      &__shortcut {
        background-color: $color-abs-stone;
        padding: 0.15rem 0.3rem;
        font-weight: normal;
      }
    }
  }

  *.is-empty:nth-child(1)::before {
    content: attr(data-empty-text);
    float: left;
    color: $color-hit-gray;
    pointer-events: none;
    height: 0;
  }

  .editor__content {
    display: flex;
    flex-grow: 1;
    padding: 0;
    height: auto;

    .ProseMirror {
      flex-grow: 1;
      outline: none;
      p {
        margin-bottom: 0;
        word-break: break-word;
        line-height: 1.5rem;
        padding: 0.2rem 0.5rem;
      }
    }

    .mention {
      background: $color-geyser;
      color: $color-trout;
      font-size: 0.8rem;
      border-radius: 1.438rem;
      padding: 0.15rem 0.5rem;
      white-space: nowrap;
    }

    .me {
      background-color: $color-picton-blue;
      color: $color-white;
    }
  }
}

.mention-label {
  border-radius: 50%;
  margin-right: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.763rem;
  min-height: 1.763rem;
  max-width: 1.763rem;
  max-height: 1.763rem;
  background-color: $color-geyser;
  color: $color-regent-gray;
}

.suggestion-list {
  max-height: 21rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0.2rem;
  border: 1px solid $color-hit-gray;
  background-color: $color-white;
  font-size: 0.8rem;

  &__no-results {
    padding: 0.2rem 0.5rem;
  }

  &__item {
    display: flex;
    white-space: nowrap;
    padding: 0.2rem 0.5rem;
    margin-bottom: 0.2rem;
    color: $color-regent-gray;
    cursor: pointer;

    &:last-child {
      margin-bottom: 0;
    }

    &.is-selected,
    &:hover {
      background-color: $color-geyser;
    }

    &.is-empty {
      opacity: 0.5;
    }

    &__label {
      max-width: 14rem;
      min-width: 14rem;
      align-items: center;
      color: $color-abs-matto;
      display: flex;

      .mention-label-name {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    &__value {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      align-self: center;
      .pipe {
        padding: 0 0.625rem;
      }
    }
  }
}
</style>
